import { useState, useEffect, useCallback } from 'react'
import { useDescricaoTecnicaVestigioService } from 'hooks/useDescricaoTecnicaVestigioService'
import { useExameConstatacaoService } from 'hooks/useExameConstatacaoService'
import { useOptionService } from 'hooks/useOptionService'
import { useFormState } from 'hooks/useFormState'

const schema = {
  resultado: {
    presence: { allowEmpty: false, message: '^O resultado da análise é obrigatório' },
  },
  subsQuims: {
    presence: { allowEmpty: false, message: '^Substância química é obrigatório' },
  },
  metodConsts: {
    presence: { allowEmpty: false, message: '^O teste empregado é obrigatório' },
  },
}

export const useDescricoesComAnalises = ({ idVestigio, idExameRealizado, definitivo }) => {
  const descricaoTecnicaService = useDescricaoTecnicaVestigioService()
  const exameConstatacaoService = useExameConstatacaoService()
  const optionService = useOptionService()

  const [descricoesTecnicas, setDescricoesTecnicas] = useState([])
  const [processandoDescricoesTecnicas, setProcessandoDescricoesTecnicas] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)

  const [tiposSubstanciasQuimicas, setTiposSubstanciasQuimicas] = useState([])
  const [processandoTiposSubstanciasQuimicas, setProcessandoTiposSubstanciasQuimicas] = useState(false)
  
  const [tiposMetodologias, setTiposMetodologias] = useState([])
  const [processandoTiposMetodologias, setProcessandoTiposMetodologias] = useState(false)

  const [openFormularioAnaliseConstatacao, setOpenFormularioAnaliseConstatacao] = useState(false)
  const [descricaoTecnicaSelecionada, setDescricaoTecnicaSelecionada] = useState(null)
  const [isFormularioInvalido, setIsFormularioInvalido] = useState(true)
  const [expandedCards, setExpandedCards] = useState({})

  const toggleCardExpansion = useCallback((id) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }, [])

  const formAnaliseConstatacao = useFormState(schema, {
    resultado: null,
    subsQuims: [],
    metodConsts: [],
    uso_proscrito_brasil: false,
    outroTipoSubstancia: '',
  })

  // Fetch active technical descriptions for the vestige, and parallel fetch their analysis
  const fetchDescricoesTecnicasDeVestigio = useCallback(async () => {
    if (idVestigio) {
      setProcessandoDescricoesTecnicas(true)
      try {
        const response = await descricaoTecnicaService.listarDescricoesTecnicasAtivasDeVestigioAsync(
          idVestigio,
          setProcessandoDescricoesTecnicas
        )

        if (response && response.length > 0) {
          const loadedWithAnalises = await Promise.all(
            response.map(async (dt) => {
              const analise = await exameConstatacaoService.listarAnaliseConstatacaoDeDescricaoEmExameAsync(
                dt.id,
                idExameRealizado
              )
              return {
                ...dt,
                analiseConstatacao: analise,
              }
            })
          )
          setDescricoesTecnicas(loadedWithAnalises)
        } else {
          setDescricoesTecnicas([])
        }
      } catch (error) {
        console.error('Error fetching technical descriptions with analysis:', error)
      } finally {
        setProcessandoDescricoesTecnicas(false)
      }
    }
  }, [idVestigio, idExameRealizado]) // Excluded descricaoTecnicaService and exameConstatacaoService to prevent loop

  // Fetch Chemical Substances options
  const fetchSubstanciasQuimicas = useCallback(async () => {
    try {
      const response = await optionService.listarOpcoesDeModeloAsync(
        'TIPO_SUBSTANCIA_QUIMICA',
        setProcessandoTiposSubstanciasQuimicas
      )
      setTiposSubstanciasQuimicas(response || [])
    } catch (error) {
      console.error('Error fetching chemical substances options:', error)
    }
  }, []) // Excluded optionService to prevent loop

  // Fetch Methodology options
  const fetchTiposMetodologias = useCallback(async () => {
    try {
      const response = await optionService.listarOpcoesDeModeloAsync(
        'TIPO_METODOLOGIA_CONSTATACAO',
        setProcessandoTiposMetodologias
      )
      setTiposMetodologias(response || [])
    } catch (error) {
      console.error('Error fetching methodology options:', error)
    }
  }, []) // Excluded optionService to prevent loop

  useEffect(() => {
    fetchDescricoesTecnicasDeVestigio()
  }, [fetchDescricoesTecnicasDeVestigio])

  useEffect(() => {
    fetchSubstanciasQuimicas()
    fetchTiposMetodologias()
  }, [fetchSubstanciasQuimicas, fetchTiposMetodologias])

  // Open formulation slide with selected item values
  const handleClickOpenFormularioAnaliseConstatacao = useCallback(
    (dt) => {
      setDescricaoTecnicaSelecionada(dt)
      const analise = dt.analiseConstatacao
      formAnaliseConstatacao.resetFormValues({
        resultado: analise?.resultado || null,
        subsQuims: analise?.subsQuims || [],
        metodConsts: analise?.metodConsts || [],
        uso_proscrito_brasil: analise?.uso_proscrito_brasil || false,
        outroTipoSubstancia: analise?.outroTipoSubstancia || '',
      })
      setOpenFormularioAnaliseConstatacao(true)
    },
    [formAnaliseConstatacao]
  )

  // Confirm/submit registry
  const handleConfirmarCadastroAnaliseConstatacao = useCallback(async () => {
    if (!descricaoTecnicaSelecionada) return
    setProcessandoOperacao(true)
    try {
      await exameConstatacaoService.cadastrarAnaliseConstatacao(
        descricaoTecnicaSelecionada.id,
        idExameRealizado,
        definitivo,
        formAnaliseConstatacao.formState.values,
        setProcessandoOperacao
      )
      
      formAnaliseConstatacao.resetFormValues({
        resultado: null,
        subsQuims: [],
        metodConsts: [],
        uso_proscrito_brasil: false,
        outroTipoSubstancia: '',
      })
      setOpenFormularioAnaliseConstatacao(false)
      setDescricaoTecnicaSelecionada(null)
      await fetchDescricoesTecnicasDeVestigio()
    } catch (error) {
      console.error('Error saving analysis details:', error)
    } finally {
      setProcessandoOperacao(false)
    }
  }, [
    descricaoTecnicaSelecionada,
    idExameRealizado,
    definitivo,
    formAnaliseConstatacao,
    fetchDescricoesTecnicasDeVestigio,
  ]) // Excluded exameConstatacaoService to prevent loop

  // Validate form state
  useEffect(() => {
    setIsFormularioInvalido(
      formAnaliseConstatacao.hasErrorWithoutTouch('resultado') ||
      formAnaliseConstatacao.hasErrorWithoutTouch('subsQuims') ||
      formAnaliseConstatacao.hasErrorWithoutTouch('metodConsts')
    )
  }, [formAnaliseConstatacao])

  const loading =
    processandoDescricoesTecnicas ||
    processandoTiposSubstanciasQuimicas ||
    processandoTiposMetodologias

  return {
    descricoesTecnicas,
    loading,
    processandoOperacao,
    tiposSubstanciasQuimicas,
    tiposMetodologias,
    openFormularioAnaliseConstatacao,
    setOpenFormularioAnaliseConstatacao,
    isFormularioInvalido,
    formAnaliseConstatacao,
    handleClickOpenFormularioAnaliseConstatacao,
    handleConfirmarCadastroAnaliseConstatacao,
    expandedCards,
    toggleCardExpansion,
  }
}
